import { createFileRoute, useLocation } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import {
  BOARD_VISIBILITIES,
  ENTERPRISE_BOARD_POLICY_RULES,
  ENTERPRISE_GUEST_INVITE_RULES,
  type BoardVisibility,
  type EnterpriseBoardPolicyRule,
  type EnterpriseBoardVisibilityPolicy,
  type EnterpriseGuestInviteRule,
  type EnterpriseMember,
  type EnterpriseRole,
} from '@molt/shared';
import {
  Avatar,
  Button,
  EmptyState,
  Menu,
  Notice,
  ProfileLink,
  RouteNotFound,
  Skeleton,
  type MenuItem,
} from '@/components';
import { profileName } from '@/components/Avatar';
import { announce } from '@/lib/announce';
import {
  useDeactivateManagedMember,
  useEnterpriseMembers,
  useEnterprisePolicy,
  useReactivateManagedMember,
  useUpdateEnterpriseBoardVisibilityPolicy,
  useUpdateEnterpriseDefaultBoardVisibility,
  useUpdateEnterpriseGuestInviteRule,
} from '@/lib/enterprise';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { signInHref, useSession } from '@/lib/session';

/**
 * `FE-67.2` and `FE-67.3` — the Enterprise policy surface, mounted.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL
 * ───────────────────────────────────────────────────────────────────────────
 * `apps/web/src/lib/enterprise.ts` has held every client function for this
 * surface since the Enterprise Administration chunk, with **no consumer** —
 * the only importer in the tree was its own unit test. `GET /enterprises/:id/
 * members` has served `enterpriseMemberListSchema` for just as long and
 * nothing read it. This route is the consumer, and nothing else about the
 * contract had to move: no migration, no server route, no event name, and no
 * change to the shape of a shipped client function.
 *
 * ⚠ ONE PAGE, TWO SECTIONS, AND THE MANAGED-MEMBER SECTION IS **NOT** A
 * FOLDED-IN SURFACE. `enterprises.$enterpriseId.power-ups.tsx` is a different
 * Enterprise page and this one is not hidden inside it — that is the folding
 * the chunk brief forbids. Policy and managed members sit together here
 * because they are one Enterprise-administration page behind one guard, about
 * one Enterprise id, and because this chunk is granted exactly one new web
 * file.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * 404, AND WHY THIS COMPONENT ASKS NO FURTHER QUESTION
 * ───────────────────────────────────────────────────────────────────────────
 * `requireEnterpriseAdmin` folds a non-admin member, an outsider and an
 * Enterprise id that never existed into **one 404** — never 401, never 403 —
 * so the route cannot be used to probe for Enterprise ids. This component
 * renders the shell's own not-found state on that 404 and **decides nothing
 * itself**: there is no `viewerEnterpriseRole` check here, no "are you an
 * admin" branch, nothing that could tell the refusals apart. A client-side
 * authorisation branch would put the answer the server deliberately withheld
 * back into the page.
 *
 * ⚠ A deactivated Enterprise admin never reaches any of this: session
 * resolution itself filters `users.deactivated_at`
 * (`apps/server/src/auth/session.ts`), so `requireUser` answers **401 before
 * the Enterprise id is looked at** and `useSession` reports the signed-out arm
 * below. That is a different layer from `requireEnterpriseAdmin`'s own
 * `deactivatedAt` filter, and the distinction is asserted in
 * `enterprise.integration.test.ts` rather than implied by a comment.
 *
 * The order inside the scope stays **404, then 403, then 409**. The 409 is
 * `FR-PERM-032`'s last-admin refusal on deactivate, and it is **rendered, not
 * pre-empted**: `assertManagedMemberRetainsAdminRows` owns that decision on
 * the server, and a client-side copy of the rule would drift from it.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * COPY: TWENTY KEYS REUSED, AND THE RULE THAT DECIDED EACH ONE
 * ───────────────────────────────────────────────────────────────────────────
 * **Reuse a key when its English is level-neutral; declare one when the
 * English names a level.** `Nobody`, `Anyone`, `Workspace admins only`,
 * `Create {visibility} boards`, `Board visibility policy`, `Admin`, `Member`
 * and the four board-audience nouns say the same thing at either level and are
 * reused verbatim. `Any workspace member` and `Workspace admins only` as
 * *board-policy* rules do **not**: at Enterprise level those two arms mean any
 * *Enterprise* member and *Enterprise* admins, so reusing the workspace words
 * would attribute the rule to the wrong level — the inversion `FR-PERM-033`'s
 * edge case exists to forbid.
 *
 * ⚠ Every table below holds CATALOGUE KEYS, never English. A module-level
 * constant cannot call `t()`, and holding the English of the moment leaves the
 * previous language on screen after the catalogue query resolves — which
 * nothing in this repository can see. The RECORDS' KEYS are wire values and
 * stay literal; only the VALUES are copy. Each `Record<Wire, string>` stays
 * explicitly typed, because that is the only thing that makes a missing arm a
 * typecheck error: `t()` returns the KEY on a miss rather than throwing, so a
 * dropped arm renders `enterpriseAdmin.boardPolicy.rule.anyMember` into the
 * page with nothing red anywhere.
 */

/** Reused from `boardTemplates.*` — the four board-audience nouns, verbatim. */
const VISIBILITY_KEYS: Record<BoardVisibility, string> = {
  private: 'boardTemplates.boardVisibility.private',
  workspace_visible: 'boardTemplates.boardVisibility.workspace',
  enterprise_visible: 'boardTemplates.boardVisibility.enterprise',
  public: 'boardTemplates.boardVisibility.public',
};

/**
 * ⚠ TWO ARMS DECLARED, ONE REUSED, and the split is the level rule above.
 * `Nobody` is the same word at both levels; `Any workspace member` is not the
 * same rule as `Any Enterprise member`.
 */
const POLICY_RULE_KEYS: Record<EnterpriseBoardPolicyRule, string> = {
  any_member: 'enterpriseAdmin.boardPolicy.rule.anyMember',
  admins_only: 'enterpriseAdmin.boardPolicy.rule.adminsOnly',
  nobody: 'workspaceSettings.boardPolicy.rule.nobody',
};

const POLICY_RULE_DESCRIPTION_KEYS: Record<EnterpriseBoardPolicyRule, string> = {
  any_member: 'enterpriseAdmin.boardPolicy.ruleDescription.anyMember',
  admins_only: 'enterpriseAdmin.boardPolicy.ruleDescription.adminsOnly',
  nobody: 'workspaceSettings.boardPolicy.ruleDescription.nobody',
};

/**
 * ⚠ THREE REUSED, ONE DECLARED, and the fourth is not a preference.
 * `ck_enterprise_settings_guest_invite_rule` admits FOUR values while the
 * workspace-level `ck_workspaces_guest_invite_rule` cannot represent
 * `same_workspace_members` — so the three shared words come from the keys the
 * workspace members route already speaks, and the fourth is declared once
 * because no catalogue key in the tree says it.
 */
const GUEST_RULE_KEYS: Record<EnterpriseGuestInviteRule, string> = {
  anyone: 'workspaceMembers.guests.rule.anyone',
  workspace_admins_only: 'workspaceMembers.guests.rule.adminsOnly',
  same_workspace_members: 'enterpriseAdmin.guestInvite.rule.sameWorkspaceMembers',
  nobody: 'workspaceMembers.guests.rule.nobody',
};

/**
 * Reused. `enterprise_members.role` is `admin | member` where
 * `workspace_members.role` is `admin | normal`; the WIRE values differ and the
 * two WORDS do not, so this record's keys are this enum's and its values are
 * the words already declared.
 */
const ROLE_LABEL_KEYS: Record<EnterpriseRole, string> = {
  admin: 'workspaceMembers.role.admin',
  member: 'workspaceMembers.role.normal',
};

export const Route = createFileRoute('/enterprises/$enterpriseId/policy')({
  component: EnterprisePolicyRoute,
});

function PageFrame({ children }: { readonly children: ReactElement }): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold text-ink">
          {t('enterpriseAdmin.route.title')}
        </h1>
        <p className="max-w-3xl text-sm text-ink-muted">
          {t('enterpriseAdmin.route.description')}
        </p>
      </header>
      {children}
    </div>
  );
}

function EnterprisePolicyRoute(): ReactElement {
  const { t } = useInterfaceLanguage();
  const { enterpriseId } = Route.useParams();
  const location = useLocation();
  const { profile, isPending, isSignedOut, error } = useSession();
  const enabled = !isSignedOut && !isPending && profile !== null;

  const policy = useEnterprisePolicy(enterpriseId, enabled);
  const members = useEnterpriseMembers(enterpriseId, enabled);
  const updateDefaultVisibility = useUpdateEnterpriseDefaultBoardVisibility(enterpriseId);
  const updateGuestInviteRule = useUpdateEnterpriseGuestInviteRule(enterpriseId);
  const updateVisibilityPolicy = useUpdateEnterpriseBoardVisibilityPolicy(enterpriseId);
  const deactivate = useDeactivateManagedMember(enterpriseId);
  const reactivate = useReactivateManagedMember(enterpriseId);

  if (isSignedOut) {
    return (
      <PageFrame>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('enterpriseAdmin.route.signInTitle')}
          body={t('enterpriseAdmin.route.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({
                pathname: location.pathname,
                search: location.searchStr,
              })}
            >
              {t('shell.signIn')}
            </Button>
          }
        />
      </PageFrame>
    );
  }

  if (isPending) {
    return (
      <PageFrame>
        <div aria-busy="true" className="flex flex-col gap-3">
          <Skeleton shape="block" count={3} />
        </div>
      </PageFrame>
    );
  }

  if (profile === null) {
    return (
      <PageFrame>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('powerUps.route.accountErrorTitle')}
          body={error?.message ?? t('powerUps.route.genericError')}
        />
      </PageFrame>
    );
  }

  /*
   * ⚠ THE DISCLOSURE ARM, and it is the load-bearing state of this page.
   *
   * Either read answering 404 means the same thing, and the page must say no
   * more than the server did: `RouteNotFound` names neither cause and renders
   * no policy form and no member list. It is deliberately OUTSIDE `PageFrame`
   * — the frame's `<h1>` names the Enterprise administration surface, and
   * printing that heading over a refusal would confirm that the id resolves to
   * something. `RouteNotFound` brings its own `<h1>`, so nesting it inside
   * would also put two rank-1 headings on one page.
   */
  if (policy.error?.status === 404 || members.error?.status === 404) {
    return <RouteNotFound />;
  }

  return (
    <PageFrame>
      <>
        <PolicySection
          policy={policy.data}
          loading={policy.isPending}
          error={policy.error?.message}
          defaultVisibilityBusy={updateDefaultVisibility.isPending}
          guestInviteBusy={updateGuestInviteRule.isPending}
          visibilityPolicyBusyKey={
            updateVisibilityPolicy.isPending
              ? (updateVisibilityPolicy.variables?.visibility ?? null)
              : null
          }
          mutationError={
            updateDefaultVisibility.error?.message ??
            updateGuestInviteRule.error?.message ??
            updateVisibilityPolicy.error?.message
          }
          onChangeDefaultVisibility={(visibility) => {
            updateDefaultVisibility.mutate(visibility, {
              onSuccess: () => {
                announce(
                  t('enterpriseAdmin.defaultVisibility.saved', {
                    visibility: t(VISIBILITY_KEYS[visibility]),
                  }),
                );
              },
            });
          }}
          onChangeGuestInviteRule={(rule) => {
            updateGuestInviteRule.mutate(rule, {
              onSuccess: () => {
                announce(
                  t('workspaceMembers.guests.ruleSaved', {
                    rule: t(GUEST_RULE_KEYS[rule]),
                  }),
                );
              },
            });
          }}
          onChangeVisibilityPolicy={(visibility, body) => {
            updateVisibilityPolicy.mutate(
              { visibility, body },
              {
                onSuccess: () => {
                  announce(
                    t('enterpriseAdmin.boardPolicy.saved', {
                      visibility: t(VISIBILITY_KEYS[visibility]),
                    }),
                  );
                },
              },
            );
          }}
        />

        <ManagedMemberSection
          members={members.data}
          loading={members.isPending}
          error={members.error?.message}
          busyUserId={
            deactivate.isPending
              ? (deactivate.variables?.userId ?? null)
              : reactivate.isPending
                ? (reactivate.variables?.userId ?? null)
                : null
          }
          /*
           * ⚠ THE 409 IS RENDERED HERE, NOT PRE-EMPTED ABOVE. Deactivating the
           * last admin of a board or workspace is refused by
           * `assertManagedMemberRetainsAdminRows` with `FR-PERM-032`'s
           * message, and this is where that message reaches the admin. The
           * page holds no copy of the rule.
           */
          mutationError={deactivate.error?.message ?? reactivate.error?.message}
          onDeactivate={(member) => {
            deactivate.mutate(
              { userId: member.id },
              {
                onSuccess: () => {
                  announce(
                    t('enterpriseAdmin.members.deactivatedAnnounce', {
                      name: profileName(member),
                    }),
                  );
                },
              },
            );
          }}
          onReactivate={(member) => {
            reactivate.mutate(
              { userId: member.id },
              {
                onSuccess: () => {
                  announce(
                    t('enterpriseAdmin.members.reactivatedAnnounce', {
                      name: profileName(member),
                    }),
                  );
                },
              },
            );
          }}
        />
      </>
    </PageFrame>
  );
}

interface PolicySectionProps {
  readonly policy:
    | {
        readonly defaultBoardVisibility: BoardVisibility;
        readonly guestInviteRule: EnterpriseGuestInviteRule;
        readonly visibilityPolicies: readonly EnterpriseBoardVisibilityPolicy[];
      }
    | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly defaultVisibilityBusy: boolean;
  readonly guestInviteBusy: boolean;
  readonly visibilityPolicyBusyKey: BoardVisibility | null;
  readonly mutationError: string | undefined;
  readonly onChangeDefaultVisibility: (visibility: BoardVisibility) => void;
  readonly onChangeGuestInviteRule: (rule: EnterpriseGuestInviteRule) => void;
  readonly onChangeVisibilityPolicy: (
    visibility: BoardVisibility,
    body: {
      readonly createRule?: EnterpriseBoardPolicyRule;
      readonly deleteRule?: EnterpriseBoardPolicyRule;
      readonly workspaceAdminCanControl?: boolean;
    },
  ) => void;
}

function PolicySection({
  policy,
  loading,
  error,
  defaultVisibilityBusy,
  guestInviteBusy,
  visibilityPolicyBusyKey,
  mutationError,
  onChangeDefaultVisibility,
  onChangeGuestInviteRule,
  onChangeVisibilityPolicy,
}: PolicySectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const boardPolicyHeading = t('workspaceSettings.boardPolicy.heading');

  return (
    <section className="flex flex-col gap-4" aria-labelledby="enterprise-policy-heading">
      <div className="flex flex-col gap-1">
        <h2
          id="enterprise-policy-heading"
          className="font-display text-lg font-semibold text-ink"
        >
          {t('enterpriseAdmin.policy.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('enterpriseAdmin.policy.description')}
        </p>
      </div>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
      {mutationError !== undefined ? <Notice tone="bad" body={mutationError} /> : null}

      {loading ? (
        <div role="status" className="flex flex-col gap-2 text-sm text-ink-muted">
          <span>{t('enterpriseAdmin.policy.loading')}</span>
          <Skeleton shape="row" count={3} />
        </div>
      ) : null}

      {policy !== undefined ? (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <ChoiceControl
              id="enterprise-default-visibility"
              label={t('enterpriseAdmin.defaultVisibility.label')}
              value={policy.defaultBoardVisibility}
              valueLabel={t(VISIBILITY_KEYS[policy.defaultBoardVisibility])}
              controlLabel={t('enterpriseAdmin.defaultVisibility.control', {
                visibility: t(VISIBILITY_KEYS[policy.defaultBoardVisibility]),
              })}
              busy={defaultVisibilityBusy}
              items={BOARD_VISIBILITIES.map((visibility) => ({
                id: visibility,
                label: t(VISIBILITY_KEYS[visibility]),
                description: undefined,
              }))}
              onSelect={(next) => {
                onChangeDefaultVisibility(next as BoardVisibility);
              }}
            />

            <ChoiceControl
              id="enterprise-guest-invite-rule"
              label={t('workspaceSettings.guests.ruleHeading')}
              value={policy.guestInviteRule}
              valueLabel={t(GUEST_RULE_KEYS[policy.guestInviteRule])}
              controlLabel={t('workspaceSettings.guests.ruleControl', {
                rule: t(GUEST_RULE_KEYS[policy.guestInviteRule]),
              })}
              busy={guestInviteBusy}
              items={ENTERPRISE_GUEST_INVITE_RULES.map((rule) => ({
                id: rule,
                label: t(GUEST_RULE_KEYS[rule]),
                description: undefined,
              }))}
              onSelect={(next) => {
                onChangeGuestInviteRule(next as EnterpriseGuestInviteRule);
              }}
            />
          </div>
          <p className="text-xs text-ink-muted">
            {t('enterpriseAdmin.guestInvite.description')}
          </p>

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-ink">{boardPolicyHeading}</h3>
            <p className="text-sm text-ink-muted">
              {t('enterpriseAdmin.boardPolicy.description')}
            </p>
          </div>

          {policy.visibilityPolicies.length === 0 ? (
            <p role="status" className="text-sm text-ink-muted">
              {t('workspaceSettings.boardPolicy.empty')}
            </p>
          ) : (
            <ul
              role="list"
              aria-label={boardPolicyHeading}
              className="flex flex-col gap-3"
            >
              {policy.visibilityPolicies.map((row) => (
                <PolicyRow
                  key={row.visibility}
                  row={row}
                  busy={visibilityPolicyBusyKey === row.visibility}
                  anyBusy={visibilityPolicyBusyKey !== null}
                  onChangeVisibilityPolicy={onChangeVisibilityPolicy}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

function PolicyRow({
  row,
  busy,
  anyBusy,
  onChangeVisibilityPolicy,
}: {
  readonly row: EnterpriseBoardVisibilityPolicy;
  readonly busy: boolean;
  readonly anyBusy: boolean;
  readonly onChangeVisibilityPolicy: PolicySectionProps['onChangeVisibilityPolicy'];
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const label = t(VISIBILITY_KEYS[row.visibility]);
  const createFieldLabel = t('workspaceSettings.boardPolicy.createLabel', {
    visibility: label,
  });
  const deleteFieldLabel = t('workspaceSettings.boardPolicy.deleteLabel', {
    visibility: label,
  });
  /*
   * ⚠ THE POLARITY. `workspace_admin_can_control` is `NOT NULL DEFAULT true`,
   * so the predicate is `=== false` and never a negation: an absent or
   * permissive value must read as PERMISSIVE. `false` only ever means an
   * Enterprise admin deliberately set it. Written as a `=== false` comparison
   * here for the same reason `WorkspaceBoardVisibilityPolicySection` writes it
   * that way — the inverted form silently locks every row.
   */
  const workspaceControlBlocked = row.workspaceAdminCanControl === false;
  const stateLabel = workspaceControlBlocked
    ? t('enterpriseAdmin.boardPolicy.workspaceControl.blocked')
    : t('enterpriseAdmin.boardPolicy.workspaceControl.allowed');

  return (
    <li
      role="listitem"
      className="flex flex-col gap-3 rounded-card border border-border-strong bg-surface-raised p-3"
    >
      <h4 className="text-base font-semibold text-ink">{label}</h4>

      <div className="grid gap-3 md:grid-cols-2">
        <ChoiceControl
          id={'enterprise-create-rule-' + row.visibility}
          label={createFieldLabel}
          value={row.createRule}
          valueLabel={t(POLICY_RULE_KEYS[row.createRule])}
          controlLabel={t('workspaceSettings.boardPolicy.ruleControl', {
            rule: t(POLICY_RULE_KEYS[row.createRule]),
            field: createFieldLabel,
          })}
          busy={busy}
          items={ENTERPRISE_BOARD_POLICY_RULES.map((rule) => ({
            id: rule,
            label: t(POLICY_RULE_KEYS[rule]),
            description: t(POLICY_RULE_DESCRIPTION_KEYS[rule]),
          }))}
          onSelect={(next) => {
            onChangeVisibilityPolicy(row.visibility, {
              createRule: next as EnterpriseBoardPolicyRule,
            });
          }}
        />

        {/* ⚠ BOTH ARMS, and the second one is not decoration.
            `enterpriseBoardVisibilityPolicySchema` puts `deleteRule` on the
            wire and `enterpriseBoardVisibilityPolicyPatchSchema` accepts it,
            so a page that read it and drew only `createRule` would leave one
            arm of `updateEnterpriseBoardVisibilityPolicy` with no consumer —
            the very defect this route exists to remove, reproduced in
            miniature. `WorkspaceBoardVisibilityPolicySection` draws both for
            the same three-rule enum, and `deleteLabel` is already declared and
            level-neutral, so the second control costs no new key. */}
        <ChoiceControl
          id={'enterprise-delete-rule-' + row.visibility}
          label={deleteFieldLabel}
          value={row.deleteRule}
          valueLabel={t(POLICY_RULE_KEYS[row.deleteRule])}
          controlLabel={t('workspaceSettings.boardPolicy.ruleControl', {
            rule: t(POLICY_RULE_KEYS[row.deleteRule]),
            field: deleteFieldLabel,
          })}
          busy={busy}
          items={ENTERPRISE_BOARD_POLICY_RULES.map((rule) => ({
            id: rule,
            label: t(POLICY_RULE_KEYS[rule]),
            description: t(POLICY_RULE_DESCRIPTION_KEYS[rule]),
          }))}
          onSelect={(next) => {
            onChangeVisibilityPolicy(row.visibility, {
              deleteRule: next as EnterpriseBoardPolicyRule,
            });
          }}
        />

        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium text-ink">
            {t('enterpriseAdmin.boardPolicy.workspaceControl.label')}
          </span>
          {/*
            ⚠ `pressed` is the PERMISSIVE state, so the toggle reads "workspace
            admins may control this" rather than "this is blocked". The click
            sends the OPPOSITE of what is drawn — `workspaceControlBlocked`
            itself — and the two must not be simplified into one another.
          */}
          <Button
            type="button"
            variant="toggle"
            pressed={!workspaceControlBlocked}
            aria-label={t('enterpriseAdmin.boardPolicy.workspaceControl.control', {
              state: stateLabel,
              visibility: label,
            })}
            busy={busy}
            disabled={anyBusy && !busy}
            onClick={() => {
              onChangeVisibilityPolicy(row.visibility, {
                workspaceAdminCanControl: workspaceControlBlocked,
              });
            }}
          >
            {stateLabel}
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * One radio `Menu` behind one `Button`, in the shape
 * `WorkspaceBoardVisibilityPolicySection` settled. The trigger renders alone
 * while `busy`, because a menu whose selection is already in flight would let
 * a second choice race the first.
 */
function ChoiceControl({
  id,
  label,
  value,
  valueLabel,
  controlLabel,
  busy,
  items,
  onSelect,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly valueLabel: string;
  readonly controlLabel: string;
  readonly busy: boolean;
  readonly items: readonly ChoiceItem[];
  readonly onSelect: (id: string) => void;
}): ReactElement {
  const trigger = (
    <Button
      type="button"
      variant="secondary"
      aria-label={controlLabel}
      disabled={busy}
      busy={busy}
    >
      {valueLabel}
    </Button>
  );

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-sm font-medium text-ink">{label}</span>
      {busy ? (
        trigger
      ) : (
        <Menu
          trigger={trigger}
          sections={[
            {
              id,
              selection: 'radio',
              value,
              onValueChange: onSelect,
              items: menuItems(items),
            },
          ]}
        />
      )}
    </div>
  );
}

interface ChoiceItem {
  readonly id: string;
  readonly label: string;
  readonly description: string | undefined;
}

function menuItems(items: readonly ChoiceItem[]): MenuItem[] {
  return items.map((item) => ({
    id: item.id,
    label: (
      <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
        <span className="text-card text-ink">{item.label}</span>
        {item.description !== undefined ? (
          <span className="text-xs text-ink-muted">{item.description}</span>
        ) : null}
      </span>
    ),
    onSelect: () => undefined,
  }));
}

interface ManagedMemberSectionProps {
  readonly members: readonly EnterpriseMember[] | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly busyUserId: string | null;
  readonly mutationError: string | undefined;
  readonly onDeactivate: (member: EnterpriseMember) => void;
  readonly onReactivate: (member: EnterpriseMember) => void;
}

function ManagedMemberSection({
  members,
  loading,
  error,
  busyUserId,
  mutationError,
  onDeactivate,
  onReactivate,
}: ManagedMemberSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <section className="flex flex-col gap-4" aria-labelledby="enterprise-members-heading">
      <div className="flex flex-col gap-1">
        <h2
          id="enterprise-members-heading"
          className="font-display text-lg font-semibold text-ink"
        >
          {t('enterpriseAdmin.members.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('enterpriseAdmin.members.description')}
        </p>
      </div>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
      {mutationError !== undefined ? <Notice tone="bad" body={mutationError} /> : null}

      {loading ? (
        <div role="status" className="flex flex-col gap-2 text-sm text-ink-muted">
          <span>{t('enterpriseAdmin.members.loading')}</span>
          <Skeleton shape="row" count={3} />
        </div>
      ) : null}

      {members?.length === 0 ? (
        <p role="status" className="text-sm text-ink-muted">
          {t('enterpriseAdmin.members.empty')}
        </p>
      ) : null}

      {members !== undefined && members.length > 0 ? (
        /* Explicit roles: Tailwind's preflight sets `list-style: none`, which
           strips the implicit `list`/`listitem` roles in WebKit/VoiceOver.
           jsdom applies no CSS and cannot see that. */
        <ul
          role="list"
          aria-label={t('enterpriseAdmin.members.listLabel')}
          className="flex flex-col gap-2"
        >
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              busy={busyUserId === member.id}
              anyBusy={busyUserId !== null}
              onDeactivate={onDeactivate}
              onReactivate={onReactivate}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function MemberRow({
  member,
  busy,
  anyBusy,
  onDeactivate,
  onReactivate,
}: {
  readonly member: EnterpriseMember;
  readonly busy: boolean;
  readonly anyBusy: boolean;
  readonly onDeactivate: (member: EnterpriseMember) => void;
  readonly onReactivate: (member: EnterpriseMember) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const name = profileName(member);
  /*
   * ⚠ `managed`, NOT membership, gates the control — and the gate is a
   * DISABLED control with a reason rather than a hidden one.
   * `assertManagedMember` requires BOTH `users.enterprise_id` matching AND the
   * account's email domain present in `enterprise_domains`, so a member who
   * holds an `enterprise_members` row but sits on an unverified domain is
   * legitimately in this list and answers 404 to a lifecycle call.
   * `FR-PERM-034` grants power over *managed* members specifically: offering
   * the button anyway would ship a control that fails, and hiding it entirely
   * would leave the admin with no account of why a colleague is listed and
   * untouchable.
   */
  const lifecycleRefusal = member.managed
    ? undefined
    : t('enterpriseAdmin.members.unmanagedReason');
  const actionLabel = member.deactivated
    ? t('enterpriseAdmin.members.reactivate')
    : t('enterpriseAdmin.members.deactivate');
  const actionControlLabel = member.deactivated
    ? t('enterpriseAdmin.members.reactivateControl', { name })
    : t('enterpriseAdmin.members.deactivateControl', { name });

  return (
    <li
      role="listitem"
      className="flex flex-col gap-1 rounded-card border border-border-strong bg-surface-raised p-3"
    >
      <div className="flex items-center gap-3">
        <Avatar profile={member} size="sm" decorative />
        <span className="min-w-0 flex-1 truncate text-ink">
          <ProfileLink profile={member} />
          {/*
            ⚠ NOT A REDACTION. `FR-PERM-034` AC3 keeps a deactivated member's
            authored content "visible and attributed", so the row keeps its
            name and its avatar and gains a state word beside them.
            Deactivation is a distinct action from removal, and the row says
            so. The state word is a SIBLING node, not appended into the name's
            text node, so `getByText` on the bare display name still matches
            exactly one node.
          */}
          {member.deactivated ? (
            <span className="ml-1 text-bad">
              {t('enterpriseAdmin.members.deactivated')}
            </span>
          ) : null}
          {member.managed ? (
            <span className="ml-1 text-ink-muted">
              {t('enterpriseAdmin.members.managed')}
            </span>
          ) : null}
        </span>

        {/* `email` is `null` unless the row is managed — a redaction the wire
            performs, rendered as absence rather than as an empty slot. */}
        {member.email !== null ? (
          <span className="hidden shrink-0 text-sm text-ink-muted sm:inline">
            {member.email}
          </span>
        ) : null}

        <span className="shrink-0 text-sm text-ink-muted">
          {t(ROLE_LABEL_KEYS[member.role])}
        </span>

        <Button
          type="button"
          variant={member.deactivated ? 'secondary' : 'danger'}
          size="sm"
          aria-label={actionControlLabel}
          disabled={!member.managed || (anyBusy && !busy)}
          disabledReason={lifecycleRefusal}
          busy={busy}
          onClick={() => {
            if (member.deactivated) onReactivate(member);
            else onDeactivate(member);
          }}
        >
          {actionLabel}
        </Button>
      </div>
    </li>
  );
}
